import { useState, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StateSelect } from "@/components/state-select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useInsured, useGetInsuredByDot, useUpdateInsured } from "@/lib/api";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneDisplay, unformatPhone } from "@/lib/formatters";
import { cn } from "@/lib/utils";

export default function InsuredEdit() {
  const [, params] = useRoute("/insureds/:id/edit");
  const insuredId = params?.id || "";
  const { data: insured, isLoading } = useInsured(insuredId);
  const updateMutation = useUpdateInsured();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [form, setForm] = useState({
    name: "",
    commercialName: "",
    dbaName: "",
    dotNumber: "",
    mcNumber: "",
    email: "",
    telephone: "",
    fax: "",
    businessOrg: "",
    companyOfficer: "",
    totalDrivers: "",
    totalPowerUnits: "",
    totalFleetSize: "",
    phyStreet: "",
    phyCity: "",
    phyState: "",
    phyZip: "",
    phyCountry: "",
    mailingStreet: "",
    mailingCity: "",
    mailingState: "",
    mailingZip: "",
    mailingCountry: "",
  });
  const [note, setNote] = useState("");
  const [dotDupMatch, setDotDupMatch] = useState<{ id: string; name: string | null } | null>(null);
  const byDot = useGetInsuredByDot(form.dotNumber.trim() || null);

  useEffect(() => {
    if (insured) {
      setForm({
        name: insured.name || "",
        commercialName: insured.commercialName || "",
        dbaName: insured.dbaName || "",
        dotNumber: insured.dotNumber || "",
        mcNumber: insured.mcNumber || "",
        email: insured.email || "",
        telephone: insured.telephone || "",
        fax: insured.fax || "",
        businessOrg: insured.businessOrg || "",
        companyOfficer: insured.companyOfficer || "",
        totalDrivers: insured.totalDrivers != null ? String(insured.totalDrivers) : "",
        totalPowerUnits: insured.totalPowerUnits != null ? String(insured.totalPowerUnits) : "",
        totalFleetSize: insured.totalFleetSize || "",
        phyStreet: insured.phyStreet || "",
        phyCity: insured.phyCity || "",
        phyState: insured.phyState || "",
        phyZip: insured.phyZip || "",
        phyCountry: insured.phyCountry || "",
        mailingStreet: insured.mailingStreet || "",
        mailingCity: insured.mailingCity || "",
        mailingState: insured.mailingState || "",
        mailingZip: insured.mailingZip || "",
        mailingCountry: insured.mailingCountry || "",
      });
    }
  }, [insured]);

  const setField = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // CSR-1140 — flag an incomplete/malformed phone (red border + message) but
  // never block saving: legacy insureds may carry a malformed phone.
  const phoneInvalid =
    form.telephone.trim() !== "" &&
    form.telephone.replace(/\D/g, "").length !== 10;

  const dotChanged =
    form.dotNumber.trim() !== (insured?.dotNumber ?? "").trim();

  const handleSubmit = async () => {
    if (updateMutation.isPending) return;

    // Only check for a DOT collision when the DOT actually changed — editing an
    // unrelated field on an insured whose DOT is unchanged (or already shares a
    // DOT with a legacy duplicate) must never trigger the warning.
    if (dotChanged && form.dotNumber.trim()) {
      const res = await byDot.refetch();
      const match = res.data as { id?: string; name?: string | null } | null;
      if (match?.id && match.id !== insuredId) {
        setDotDupMatch({ id: match.id, name: match.name ?? null });
        return;
      }
    }
    performUpdate();
  };

  const performUpdate = async () => {
    setDotDupMatch(null);
    try {
      await updateMutation.mutateAsync({
        id: insuredId,
        data: {
          name: form.name || null,
          commercialName: form.commercialName || null,
          dbaName: form.dbaName || null,
          dotNumber: form.dotNumber || null,
          mcNumber: form.mcNumber || null,
          email: form.email || null,
          telephone: form.telephone || null,
          fax: form.fax || null,
          businessOrg: form.businessOrg || null,
          companyOfficer: form.companyOfficer || null,
          totalDrivers: form.totalDrivers ? parseInt(form.totalDrivers, 10) : null,
          totalPowerUnits: form.totalPowerUnits ? parseInt(form.totalPowerUnits, 10) : null,
          totalFleetSize: form.totalFleetSize || null,
          phyStreet: form.phyStreet || null,
          phyCity: form.phyCity || null,
          phyState: form.phyState || null,
          phyZip: form.phyZip || null,
          phyCountry: form.phyCountry || null,
          mailingStreet: form.mailingStreet || null,
          mailingCity: form.mailingCity || null,
          mailingState: form.mailingState || null,
          mailingZip: form.mailingZip || null,
          mailingCountry: form.mailingCountry || null,
        },
        note: note.trim() || undefined,
      });
      toast({ title: "Insured Updated", description: "Insured has been updated successfully." });
      navigate(`/insureds/${params?.id}`);
    } catch {
      toast({ title: "Update Failed", description: "Could not update the insured.", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!insured) {
    return (
      <Layout>
        <div className="space-y-6">
          <Button variant="ghost" className="gap-2 pl-0" onClick={() => window.history.back()}>
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-lg font-medium">Insured not found</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">Edit Insured</h1>
          </div>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending} className="gap-2">
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        </div>

        {dotDupMatch && (
          <div
            data-testid="insured-edit-dot-dup-warning"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
          >
            <p>
              Another insured already has this DOT number:{" "}
              <strong>{dotDupMatch.name ?? "(no name)"}</strong>. Do you want to
              update this insured anyway?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="cursor-pointer"
                data-testid="insured-edit-dot-dup-update-anyway"
                disabled={updateMutation.isPending}
                onClick={performUpdate}
              >
                Update anyway
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="cursor-pointer"
                data-testid="insured-edit-dot-dup-cancel"
                disabled={updateMutation.isPending}
                onClick={() => setDotDupMatch(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Company Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-2")}>
              <div className="space-y-1">
                <Label htmlFor="name" className="text-xs">Company Name</Label>
                <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="commercialName" className="text-xs">Commercial Name</Label>
                <Input id="commercialName" value={form.commercialName} onChange={(e) => setField("commercialName", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dbaName" className="text-xs">DBA Name</Label>
                <Input id="dbaName" value={form.dbaName} onChange={(e) => setField("dbaName", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dotNumber" className="text-xs">DOT Number</Label>
                <Input id="dotNumber" value={form.dotNumber} onChange={(e) => { setField("dotNumber", e.target.value); setDotDupMatch(null); }} className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mcNumber" className="text-xs">MC Number</Label>
                <Input id="mcNumber" value={form.mcNumber} onChange={(e) => setField("mcNumber", e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="businessOrg" className="text-xs">Business Type</Label>
                <Input id="businessOrg" value={form.businessOrg} onChange={(e) => setField("businessOrg", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="companyOfficer" className="text-xs">Company Officer</Label>
                <Input id="companyOfficer" value={form.companyOfficer} onChange={(e) => setField("companyOfficer", e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-3")}>
              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="telephone" className="text-xs">Phone</Label>
                <Input id="telephone" value={formatPhoneDisplay(form.telephone)} onChange={(e) => setField("telephone", unformatPhone(e.target.value))} maxLength={14} placeholder="(555) 123-4567" className={cn(phoneInvalid && "border-destructive")} />
                {phoneInvalid && (
                  <p data-testid="insured-edit-phone-error" className="text-xs text-destructive">
                    Enter a valid 10-digit phone number.
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="fax" className="text-xs">Fax</Label>
                <Input id="fax" value={form.fax} onChange={(e) => setField("fax", e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fleet Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fleet Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-3")}>
              <div className="space-y-1">
                <Label htmlFor="totalDrivers" className="text-xs">Total Drivers</Label>
                <Input id="totalDrivers" type="number" value={form.totalDrivers} onChange={(e) => setField("totalDrivers", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="totalPowerUnits" className="text-xs">Total Power Units</Label>
                <Input id="totalPowerUnits" type="number" value={form.totalPowerUnits} onChange={(e) => setField("totalPowerUnits", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="totalFleetSize" className="text-xs">Total Fleet Size</Label>
                <Input id="totalFleetSize" value={form.totalFleetSize} onChange={(e) => setField("totalFleetSize", e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Physical Address */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Physical Address</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-2")}>
              <div className="space-y-1 col-span-full">
                <Label htmlFor="phyStreet" className="text-xs">Street</Label>
                <Input id="phyStreet" value={form.phyStreet} onChange={(e) => setField("phyStreet", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phyCity" className="text-xs">City</Label>
                <Input id="phyCity" value={form.phyCity} onChange={(e) => setField("phyCity", e.target.value)} />
              </div>
              <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-3")}>
                <div className="space-y-1">
                  <Label htmlFor="phyState" className="text-xs">State</Label>
                  <StateSelect id="phyState" value={form.phyState} onChange={(v) => setField("phyState", v)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="phyZip" className="text-xs">ZIP</Label>
                  <Input id="phyZip" value={form.phyZip} onChange={(e) => setField("phyZip", e.target.value)} maxLength={10} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="phyCountry" className="text-xs">Country</Label>
                  <Input id="phyCountry" value={form.phyCountry} onChange={(e) => setField("phyCountry", e.target.value)} maxLength={2} placeholder="US" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mailing Address */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mailing Address</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-2")}>
              <div className="space-y-1 col-span-full">
                <Label htmlFor="mailingStreet" className="text-xs">Street</Label>
                <Input id="mailingStreet" value={form.mailingStreet} onChange={(e) => setField("mailingStreet", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mailingCity" className="text-xs">City</Label>
                <Input id="mailingCity" value={form.mailingCity} onChange={(e) => setField("mailingCity", e.target.value)} />
              </div>
              <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-3")}>
                <div className="space-y-1">
                  <Label htmlFor="mailingState" className="text-xs">State</Label>
                  <StateSelect id="mailingState" value={form.mailingState} onChange={(v) => setField("mailingState", v)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailingZip" className="text-xs">ZIP</Label>
                  <Input id="mailingZip" value={form.mailingZip} onChange={(e) => setField("mailingZip", e.target.value)} maxLength={10} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailingCountry" className="text-xs">Country</Label>
                  <Input id="mailingCountry" value={form.mailingCountry} onChange={(e) => setField("mailingCountry", e.target.value)} maxLength={2} placeholder="US" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Note */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-2">
              <Label htmlFor="edit-note">Change Note (optional)</Label>
              <Textarea
                id="edit-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Describe why you're making this change..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Bottom Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={updateMutation.isPending} className="gap-2">
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        </div>
      </div>
    </Layout>
  );
}
